import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import MenuViewer from './components/MenuViewer';
import AdminDashboard from './components/AdminDashboard';
import AdminLogin from './components/AdminLogin';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
// Import new pages
import LandingPage from './pages/LandingPage';
import ContactPage from './pages/ContactPage';
import PricingPage from './pages/PricingPage';
import AboutPage from './pages/AboutPage';
import FAQPage from './pages/FAQPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import Navbar from './components/Navbar';

// Protected Route component for admin
const ProtectedAdminRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Check if user is authenticated
    const adminAuth = sessionStorage.getItem('adminAuthenticated');
    setIsAuthenticated(adminAuth === 'true');
    setChecking(false);
  }, []);

  if (checking) {
    // Still checking authentication status
    return null;
  }

  if (!isAuthenticated) {
    // Not authenticated, redirect to login
    return <AdminLogin onLogin={() => setIsAuthenticated(true)} />;
  }

  // Authenticated, render the protected component
  return children;
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    button: {
      fontWeight: 500,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '50px',
          padding: '10px 24px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(45deg, #1976d2 30%, #2196f3 90%)',
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <ScrollToTop />
        <ErrorBoundary>          <Routes>
            <Route path="/menu/:restaurantId" element={<MenuViewer />} />
            <Route
              path="/admin"
              element={
                <ProtectedAdminRoute>
                  <AdminDashboard />
                </ProtectedAdminRoute>
              }
            />
            {/* New Routes */}
            <Route path="/" element={<><Navbar /><LandingPage /></>} />
            <Route path="/contact" element={<><Navbar /><ContactPage /></>} />
            <Route path="/pricing" element={<><Navbar /><PricingPage /></>} />
            <Route path="/about" element={<><Navbar /><AboutPage /></>} />
            <Route path="/faq" element={<><Navbar /><FAQPage /></>} />
            <Route path="/privacy-policy" element={<><Navbar /><PrivacyPolicyPage /></>} />
            <Route path="/terms-of-service" element={<><Navbar /><TermsOfServicePage /></>} />
          </Routes>
        </ErrorBoundary>
      </Router>
    </ThemeProvider>
  );
}

export default App;
